import { useRouter } from 'found';
import styled from 'styled-components';
import React, { useState } from 'react';
import { makeMutation } from '../../../../api/helpers';
import ContentCenterer from '../../../../components/ContentCenterer';
import Button from '../../../../components/input/Button';
import TextInput from '../../../../components/input/TextInput';
import Spacer from '../../../../components/Spacer';
import { Buttons } from '../Buttons';
import { useToastDispatch } from '../../../../components/Toast/ToastProvider';
import { createFragmentContainer, graphql } from 'react-relay';
import { UpdateCharge_charge } from './__generated__/UpdateCharge_charge.graphql';
import {
  DeleteCharge,
  UpdateCharge as UpdateChargeMutation,
} from '../../../../api/mutations';
import { HelpText } from '../../../../components/HelpText';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronDown } from '@fortawesome/free-solid-svg-icons';

const MainInfo = styled.div`
  display: flex;
  justify-content: center;
  flex-direction: column;
  align-items: center;
`;

const ChargeType = styled.div`
  font-weight: 600;
  color: #5d6ef8;
  font-size: 18px;
  letter-spacing: 1.2px;
  text-transform: uppercase;
`;

const PricingContainer = styled.div`
  display: flex;
  align-items: center;
`;

const Price = styled.div`
  font-size: 34px;
  font-weight: 500;
  display: flex;
  align-items: center;
`;

const Metric = styled.div`
  font-size: 14px;
  color: #9092c1;
  font-weight: 600;
`;

const Options = styled.div``;

const OptionsHeader = styled.div`
  display: flex;
  justify-content: space-between;
  color: #9092c1;
  font-size: 13px;
  font-weight: 500;
  transition: all 0.2s;
  border-radius: 6px;
  padding: 20px;
  cursor: pointer;
  &:hover {
    background: #f8f9fe;
  }
`;

const OptionsText = styled.div``;

const DropIcon = styled.div<{ dropped: Boolean }>`
  transition: all 0.2s;
  transform: rotate(${(props) => (props.dropped ? '180deg' : '0deg')});
`;

const OptionsContent = styled.div<{ dropped: Boolean }>`
  margin: 10px;
  overflow: hidden;
  transition: all 0.2s;
  max-height: ${(props) => (props.dropped ? '1000px' : '0px')};
`;

const DeleteChargeCont = styled.div`
  max-width: 300px;
`;

const ChargeCurrency = styled.div`
  // color: #9094ac;
  font-size: 14px;
  color: #9092c1;
  font-weight: 600;
`;

const Container = styled.div`
  display: flex;
  justify-content: center;
  flex: 1;
`;

type Props = {
  charge: UpdateCharge_charge;
};

function UpdateCharge({ charge }: Props) {
  const [formState, setFormState] = useState({
    name: charge.name,
    description: charge.description,
    price: 0,
    metricName: '',
  });

  const [otherOptionsDropped, setOtherOptionsDropped] = useState(false);

  const { router } = useRouter();
  const dispatch = useToastDispatch();

  // onClose go back to previous page
  const onClose = () => {
    router.go(-1);
  };

  const onSave = async () => {
    if (formState.name.replace(' ', '') === '') {
      dispatch({ text: 'Please enter a charge name', alert: 'error' });
      return;
    }

    if (formState.description.replace(' ', '') === '') {
      dispatch({ text: 'Please enter a charge description', alert: 'error' });
      return;
    }

    const variables = {
      chargeID: charge.id,
      name: formState.name,
      description: formState.description,
    };

    try {
      await makeMutation(UpdateChargeMutation, variables);
      dispatch({
        text: `'${formState.name}' charge updated successfully`,
        alert: 'success',
      });
      onClose();
    } catch (err) {
      dispatch({ text: err.toString(), alert: 'error', stayFor: 4000 });
    }
  };

  const onDelete = async () => {
    if (!window.confirm('Are you sure you want to delete this charge')) return;

    const variables = {
      chargeID: charge.id,
    };

    try {
      await makeMutation(DeleteCharge, variables);
      dispatch({
        text: `'${formState.name}' charge deleted`,
        alert: 'success',
      });
      onClose();
    } catch (err) {
      dispatch({ text: err.toString(), alert: 'error', stayFor: 4000 });
    }
  };

  return (
    <Container>
      <ContentCenterer maxWidth={'838px'}>
        <Spacer spacing={4} vertical />
        <MainInfo>
          <ChargeType>{charge.chargeType}</ChargeType>
          <Spacer vertical spacing={2} />
          <PricingContainer>
            <Price>
              {charge.prices.map((priceInfo: any) => (
                <>
                  {priceInfo.amount > 0 ? `${priceInfo.amount}` : 'Free'}
                  <Spacer horizontal spacing={1} />
                  <ChargeCurrency>{priceInfo.currency}</ChargeCurrency>
                  <Spacer horizontal spacing={2} />
                </>
              ))}
            </Price>
            {charge.chargeType === 'dynamic' && (
              <>
                <Spacer horizontal spacing={1} />
                <Metric>&nbsp;/&nbsp;{charge.metricName}</Metric>
              </>
            )}
          </PricingContainer>
        </MainInfo>

        <Spacer vertical spacing={4} />
        <HelpText type='help'>
          Charge prices cannot be changed once created
        </HelpText>
        <Spacer vertical spacing={2} />
        <TextInput
          label={'NAME'}
          placeholder={'e.g File upload charge'}
          value={formState.name}
          onChange={(text) => setFormState((val) => ({ ...val, name: text }))}
        />
        <Spacer vertical spacing={2} />
        <TextInput
          label={'DESCRIPTION'}
          placeholder={'e.g Cost per file uploaded'}
          value={formState.description}
          onChange={(text) =>
            setFormState((val) => ({ ...val, description: text }))
          }
        />
        <Spacer vertical spacing={2} />
        <Options>
          <OptionsHeader
            onClick={() => setOtherOptionsDropped(!otherOptionsDropped)}
          >
            <OptionsText>OTHER OPTIONS</OptionsText>
            <DropIcon dropped={otherOptionsDropped}>
              <FontAwesomeIcon icon={faChevronDown} />
            </DropIcon>
          </OptionsHeader>
          <Spacer vertical spacing={2} />
          <OptionsContent dropped={otherOptionsDropped}>
            <DeleteChargeCont>
              <HelpText type='warning'>
                Ensure you are not using this charge in your application before
                deleting
              </HelpText>
              <Spacer vertical spacing={2} />
              <Button background={'#F85C68'} color={'white'} onClick={onDelete}>
                Delete charge
              </Button>
            </DeleteChargeCont>
            <Spacer vertical spacing={2} />
          </OptionsContent>
        </Options>
        <Spacer vertical spacing={4} />
        <Buttons>
          <Button background={'#bab8c5'} color={'white'} onClick={onClose}>
            Cancel
          </Button>
          <Button color={'white'} onClick={onSave}>
            Update
          </Button>
        </Buttons>
      </ContentCenterer>
    </Container>
  );
}

export default createFragmentContainer(UpdateCharge, {
  charge: graphql`
    fragment UpdateCharge_charge on Charge {
      id
      chargeType
      name
      description
      chargeIdentifier
      prices {
        amount
        currency
      }
      metricName
    }
  `,
});
